'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import { useEffect, useRef, useState, type HTMLAttributes } from 'react'
import { cn } from '../../utils/cn'
import { Tooltip } from '../overlays/tooltip'

/**
 * Body text.
 *
 * Numeric sizes are inherited from the donor on purpose: 793 call sites pass
 * `size={4}`, and renaming them would turn a mechanical migration into a manual
 * one for no benefit the reader can see.
 *
 * The colour names are not inherited. The donor used appearance names (`black`,
 * `darkGrey`, `purple`), which a tenant theme cannot honour: there is no such
 * thing as a black that repaints. These are tones, which it can.
 */
export const paragraphVariants = cva('font-sans', {
    variants: {
        tone: {
            default: 'text-foreground',
            muted: 'text-foreground-muted',
            subtle: 'text-foreground-disabled',
            onColor: 'text-foreground-brand',
            success: 'text-foreground-success',
            warning: 'text-foreground-warning',
            danger: 'text-foreground-danger',
            info: 'text-foreground-info'
        },
        size: {
            // The line height rides with the size rather than sitting in the
            // base. A Tailwind font-size utility carries a line-height of its
            // own, so a merge keeps whichever of the two comes last.
            1: 'text-aria-xxs leading-[150%]',
            2: 'text-aria-xs leading-[150%]',
            3: 'text-aria-sm leading-[150%]',
            4: 'text-aria-base leading-[150%]',
            5: 'text-aria-lg leading-[150%]',
            6: 'text-aria-xl leading-[150%]',
            7: 'text-aria-2xl leading-[150%]'
        },
        weight: {
            regular: 'font-normal',
            medium: 'font-medium'
        },
        isTruncated: {
            true: 'block truncate',
            false: ''
        }
    },
    defaultVariants: { tone: 'default', size: 4, weight: 'regular', isTruncated: false }
})

export type ParagraphProps = Omit<HTMLAttributes<HTMLParagraphElement>, 'className' | 'style' | 'color'> &
    VariantProps<typeof paragraphVariants>

/**
 * Reveals the full text of a paragraph that does not fit.
 *
 * Truncation hides content, so on its own it costs the reader the part that was
 * cut. The tooltip gives it back on hover, which is what a table cell needs: the
 * column sets the width, and the text has no say in it.
 *
 * The measurement watches the element rather than the children, because a
 * column that narrows starts to truncate with no new render. It reads
 * `textContent` rather than the children, since the tooltip takes a string and
 * arbitrary nodes would leave it saying nothing.
 */
const useOverflowTooltip = (isEnabled: boolean) => {
    const ref = useRef<HTMLParagraphElement>(null)
    const [overflowText, setOverflowText] = useState<string | null>(null)

    useEffect(() => {
        const element = ref.current
        if (!isEnabled || !element) return

        const measure = () => {
            const isOverflowing = element.scrollWidth > element.clientWidth
            setOverflowText(isOverflowing ? element.textContent : null)
        }

        measure()

        // The element rather than the children: a column that narrows starts to
        // truncate with no new render. The children are watched separately,
        // because their identity changes on every render and depending on it
        // would re-run this effect forever.
        const resize = new ResizeObserver(measure)
        resize.observe(element)

        const mutation = new MutationObserver(measure)
        mutation.observe(element, { childList: true, subtree: true, characterData: true })

        return () => {
            resize.disconnect()
            mutation.disconnect()
        }
    }, [isEnabled])

    return { ref, overflowText }
}

export const Paragraph = ({ tone, size, weight, isTruncated, ...props }: ParagraphProps) => {
    const { ref, overflowText } = useOverflowTooltip(isTruncated === true)

    const paragraph = <p ref={ref} className={cn(paragraphVariants({ tone, size, weight, isTruncated }))} {...props} />

    if (isTruncated !== true) return paragraph

    // Always wrapped once truncation is on, and shut when there is nothing to
    // reveal. Wrapping only while overflowing would remount the paragraph, and
    // the next measurement would read the remounted element instead.
    return (
        <Tooltip content={overflowText ?? ''} isDisabled={overflowText === null}>
            {paragraph}
        </Tooltip>
    )
}

/**
 * A heading.
 *
 * `level` sets the tag and `size` sets the appearance, deliberately separated.
 * The donor rendered an `<h1>` at every size, so a page could carry a dozen
 * top-level headings and a screen reader had no outline to navigate. Size is a
 * design choice; level is a document-structure choice, and they are not the
 * same decision.
 */
export const titleVariants = cva('font-sans tracking-tight', {
    variants: {
        tone: {
            default: 'text-foreground',
            muted: 'text-foreground-muted',
            onColor: 'text-foreground-brand',
            success: 'text-foreground-success',
            warning: 'text-foreground-warning',
            danger: 'text-foreground-danger'
        },
        size: {
            // `sm` and `base` exist because a card header is a heading and is
            // not display type. They expose steps the scale already has rather
            // than adding any.
            sm: 'text-aria-sm leading-tight',
            base: 'text-aria-base leading-tight',
            lg: 'text-aria-lg leading-tight',
            xl: 'text-aria-xl leading-tight',
            '2xl': 'text-aria-2xl leading-tight',
            '3xl': 'text-aria-3xl leading-tight'
        },
        weight: {
            normal: 'font-normal',
            medium: 'font-medium'
        }
    },
    defaultVariants: { tone: 'default', size: 'xl', weight: 'medium' }
})

export type TitleProps = Omit<HTMLAttributes<HTMLHeadingElement>, 'className' | 'style' | 'color'> &
    VariantProps<typeof titleVariants> & {
        /** Document level. Pick it from the page outline, not from the size. */
        level: 1 | 2 | 3 | 4
    }

export const Title = ({ level, tone, size, weight, ...props }: TitleProps) => {
    const Heading = `h${level}` as const

    return <Heading className={cn(titleVariants({ tone, size, weight }))} {...props} />
}
